import InformationTextsModal from '@/components/InformationTextsModal';
import PriceDisplay from '@/components/PriceDisplay';
import PromoCode from '@/components/PromoCode';
import { useTranslation } from '@/i18n';
import { TransText } from '@/i18n/trans/text';
import type { AdmissionDTO } from '@/types/dto/agent';
import { cn } from '@/utils/cn';
import {
  Accordion,
  Button,
  Icons,
  Typography,
} from '@packages/webshop-design-system';
import { useState, type FC, type ReactNode } from 'react';

interface AdmissionItemProps {
  icon: keyof typeof Icons;
  description?: ReactNode;
  amount: number;
  status: AdmissionDTO['status'];
  promoCode?: {
    name?: string;
    code?: string;
    description?: string;
    type?: string;
    number?: string;
  };
  descriptiveTexts?: Array<{
    description?: string;
    textElementType?: string;
  }>;
}

const AdmissionItem: FC<AdmissionItemProps> = ({
  icon,
  description,
  amount,
  status,
  promoCode,
  descriptiveTexts,
}) => {
  const { t } = useTranslation();
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const IconComponent = Icons[icon];
  const isRefunded = status === 'REFUNDED';
  const isReleased = status === 'RELEASED';
  const isExchanged = status === 'EXCHANGED';
  const isExchangeOngoing = status === 'EXCHANGE_ONGOING';
  const isGrayedOut =
    isRefunded || isReleased || isExchanged || isExchangeOngoing;

  // Clicking the info icon opens the information-texts modal — the same one
  // as the offer card's "Read more" — instead of a tooltip (BR-57160)
  const informationTexts =
    descriptiveTexts
      ?.filter(
        (text) =>
          !!text.description &&
          (text.textElementType === undefined ||
            text.textElementType === 'INFORMATION')
      )
      .map((text) => text.description!) ?? [];

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-start justify-between gap-2">
        <div className="flex gap-2">
          <IconComponent
            className={cn('h-4 w-4 text-primary', {
              'text-neutral': isGrayedOut,
            })}
          />
          <Typography
            variant="body2-bold"
            className={cn({
              'text-neutral': isGrayedOut,
            })}
          >
            {description}
            {!!informationTexts.length && (
              <>
                <Button
                  variant="ghost"
                  data-testid="admission-descriptive-texts"
                  aria-label={t(
                    'text.productInformation',
                    'Product information'
                  )}
                  className="ml-1 h-auto p-0 align-middle"
                  onPress={() => setIsInfoModalOpen(true)}
                >
                  <Icons.infoCircle className="h-4 w-4 text-primary" />
                </Button>
                <InformationTextsModal
                  title={description}
                  amount={amount}
                  descriptions={informationTexts}
                  isOpen={isInfoModalOpen}
                  onOpenChange={setIsInfoModalOpen}
                  data-testid="admission-information-modal"
                />
              </>
            )}
          </Typography>
        </div>
        <Typography
          variant="body2-bold"
          className={cn('text-primary', {
            'text-neutral': isGrayedOut,
          })}
        >
          <PriceDisplay amount={amount} />
        </Typography>
      </div>
      {/* A travel pass registered on the admission arrives through
          appliedReductions like promo codes do, but it is not one — label
          it "Travel pass: {number}" per design instead of "Promo code"
          (BR-54939). */}
      {promoCode?.type === 'TRAVEL_PASS' &&
        (promoCode.number || promoCode.code) && (
          <div className="ml-6 w-fit rounded-sm border border-neutral-light px-2 py-1">
            <Typography variant="body2">
              <span className="text-neutral">
                <TransText i18nKey="travelPass" />
                {': '}
              </span>
              <Typography variant="body2-bold" as="span">
                {promoCode.number || promoCode.code}
              </Typography>
            </Typography>
          </div>
        )}
      {promoCode?.type !== 'TRAVEL_PASS' &&
        promoCode &&
        (promoCode.code || promoCode.name) && (
          <Accordion
            className="ml-6 flex flex-col gap-1 border-none"
            classNames={{ trigger: 'p-0 hover:no-underline' }}
            trigger={
              promoCode.code ? (
                <Typography variant="body2-bold">
                  <span className="text-success-text">
                    <TransText i18nKey="promoCode" />
                    {': '}
                  </span>
                  <Typography
                    variant="body2"
                    className="text-primary"
                    as="span"
                  >
                    {promoCode.code}
                  </Typography>
                </Typography>
              ) : (
                <Typography variant="body2-bold">
                  <span className="text-success-text">
                    <TransText i18nKey="discount" />
                    {': '}
                  </span>
                  <Typography
                    variant="body2"
                    className="text-primary"
                    as="span"
                  >
                    {promoCode.name}
                  </Typography>
                </Typography>
              )
            }
          >
            <PromoCode {...promoCode} />
          </Accordion>
        )}
      {isRefunded && (
        <Typography
          variant="label"
          className="ml-6 w-fit rounded-sm bg-neutral px-1 py-0.5 text-white"
        >
          <TransText i18nKey="refunded" />
        </Typography>
      )}
      {isReleased && (
        <Typography
          variant="label"
          className="ml-6 w-fit rounded-sm bg-neutral px-1 py-0.5 text-white"
        >
          <TransText i18nKey="cancelled" />
        </Typography>
      )}
      {isExchanged && (
        <Typography
          variant="label"
          className="ml-6 w-fit rounded-sm bg-neutral px-1 py-0.5 text-white"
        >
          <TransText i18nKey="exchanged" />
        </Typography>
      )}
    </div>
  );
};

export default AdmissionItem;
